import { FC, FormEventHandler, ReactNode } from "react";
import { Box, Button, CircularProgress, Grid, Paper, Typography } from "@mui/material";

export const FormContainer: FC<{ onSubmit: FormEventHandler<HTMLFormElement> }> = ({ onSubmit, children }) => (
  <Box
    component="form"
    display="flex"
    flexDirection="column"
    gap={{ xs: 1.5, sm: 2 }}
    sx={{ maxWidth: 1100, py: { xs: 0, sm: 2 } }}
    autoComplete="off"
    onSubmit={onSubmit}
  >
    {children}
  </Box>
);

type FormHeaderProps = {
  section: string;
  title: ReactNode;
  isSubmitting: boolean;
};

// Sticky top bar (below the app bar) with breadcrumb, title, extra controls (children) and the submit button.
// On phones the extra controls move to their own row below the title and save button.
export const FormHeader: FC<FormHeaderProps> = ({ section, title, isSubmitting, children }) => (
  <Paper
    variant="outlined"
    sx={{
      position: "sticky",
      top: { xs: 56, sm: 64 },
      zIndex: 2,
      px: { xs: 1.5, sm: 2 },
      py: { xs: 1.25, sm: 1.5 },
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: 2,
      rowGap: 0.75,
      boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{section}</Typography>
      <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>{title}</Typography>
    </Box>
    {children && (
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        sx={{
          order: { xs: 3, sm: 0 },
          width: { xs: "100%", sm: "auto" },
          borderTop: { xs: 1, sm: 0 },
          borderColor: "divider",
          pt: { xs: 0.5, sm: 0 },
        }}
      >
        {children}
      </Box>
    )}
    <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ minWidth: 96, flex: "none" }}>
      {isSubmitting ? <CircularProgress size={22} color="inherit" /> : "Lagre"}
    </Button>
  </Paper>
);

export const FormSection: FC<{ title: string }> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: { xs: 1.5, sm: 2 } }}>{title}</Typography>
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      {children}
    </Grid>
  </Paper>
);
